import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useMeContext } from '../api/me-provider.js';
import { setupHash } from '../api/athlete-sign-in.js';
import { claimFamilyInvitation } from '../api/family-invitation.js';
import { ApiError } from '../api/client.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * /welcome?token=… — where the invitation's Sign up lands (stage C; canvas row
 * 11, approved 16 October).
 *
 * Signed in or signed up first, as every route is; ProfileGate lets this one
 * through without a profile, because it is what gives the parent the one the
 * coach started. The family is claimed at once, and the parent is taken to the
 * athlete to finish - where the gate shows the profile form first, filled in
 * from the coach's words, and then the athlete. With nobody left to finish, Home.
 *
 * Only the address the coach typed can claim it (ruled 16 October). Anyone else
 * is told which address the link is for, and nothing else.
 *
 * A second parent's link (/join, canvas row D) lands here too, with its own
 * claim: joined, they go to Account, where the gate shows their profile form
 * first (filled in from what the first parent typed).
 */

type Outcome =
  | { status: 'claiming' }
  | { status: 'dead' }
  | { status: 'other'; email: string }
  | { status: 'has-account' }
  | { status: 'failed' };

/** Claims what the link is for; answers where to go next. */
export type Claim = (getToken: () => Promise<string | null>, hash: string) => Promise<string>;

const claimFamily: Claim = async (getToken, hash) => {
  const claimed = await claimFamilyInvitation(getToken, hash);
  return claimed.athlete_client_id ? `/account/people/${claimed.athlete_client_id}` : '/';
};

export function WelcomeRoute({ claim = claimFamily }: { claim?: Claim }) {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { reload } = useMeContext();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [outcome, setOutcome] = useState<Outcome>({ status: 'claiming' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!/^[A-Za-z0-9_-]{20,}$/.test(token)) {
      setOutcome({ status: 'dead' });
      return;
    }
    setOutcome({ status: 'claiming' });
    void (async () => {
      try {
        const next = await claim(getToken, await setupHash(token));
        if (cancelled) return;
        reload();
        navigate(next, { replace: true });
      } catch (cause) {
        if (cancelled) return;
        const code = cause instanceof ApiError ? cause.code : '';
        if (code === 'other_email' && cause instanceof ApiError && cause.email) {
          setOutcome({ status: 'other', email: cause.email });
        } else if (code === 'link_dead') {
          setOutcome({ status: 'dead' });
        } else if (code === 'has_account') {
          setOutcome({ status: 'has-account' });
        } else {
          setOutcome({ status: 'failed' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, getToken, navigate, reload, nonce, claim]);

  if (outcome.status === 'claiming') return <WaitNote>Checking…</WaitNote>;

  if (outcome.status === 'failed') {
    return (
      <>
        <p className="state-note" data-tone="warn">
          That did not work. Try again.
        </p>
        <button type="button" className="primary" onClick={() => setNonce((n) => n + 1)}>
          Try again
        </button>
      </>
    );
  }

  return (
    <section className="panel">
      <h2>
        {outcome.status === 'other'
          ? `This link is for ${outcome.email}`
          : outcome.status === 'has-account'
            ? 'This email already has an account'
            : 'This link no longer works'}
      </h2>
    </section>
  );
}
